import { useParams } from 'react-router';
import { Box, Grid, Stack, Typography, Divider, Switch } from '@mui/material';
import { useGetVacancyById } from '@area/vacancy/hooks/useGetVacancyById';
import colors from '@styles/colors.module.scss';
import indents from '@styles/indents.module.scss';
import { useVacancyControllerTogglePublishMutation, VacancyResponse } from '@api/admin/adminGeneratedApi'; // заменить на реальный тип

import { useGetUniversities } from '@area/vacancies/components/VacanciesTable/hooks/useGetUniversities';
import { useGetSpecialties } from '@area/vacancies/components/VacanciesTable/hooks/useGetSpecialties';
import { useGetForms } from '@area/vacancies/components/VacanciesTable/hooks/useGetForms';
import { useGetStatuses } from '@area/vacancies/components/VacanciesTable/hooks/useGetStatuses';
import { useGetVacancyStatisticById } from '@area/vacancy/hooks/useGetVacancyStatisticById';
import { LoadingSpinner } from '@components/LoadingSpinner';

import { VacancyStatistics } from '../VacancyStatistics';
import styles from './styles.module.scss';

const fieldOptions: {
  field: Partial<keyof VacancyResponse | string>;
  title: string;
  formatter?: (value: any, record: VacancyResponse) => React.ReactNode;
  isCalculated?: boolean;
}[] = [
  { field: 'name', title: 'Название вакансии' },
  {
    field: 'salaryRange',
    title: 'Вилка заработной платы',
    formatter: (_value, record) => (record ? `${record?.salary_min} - ${record?.salary_max}` : 'Не указано'),
    isCalculated: true,
  },
  { field: 'company_name', title: 'Название компании нанимателя' },
  { field: 'company_info', title: 'Информация о компании нанимателе' },
  { field: 'area', title: 'Город' },
  { field: 'work_schedule', title: 'График работы' },
  { field: 'employment_type', title: 'Тип искомого кандидата' },
  { field: 'description', title: 'Описание вакансии' },
  { field: 'conditions', title: 'Условия', formatter: (value) => (value ? value.join(', ') : 'Не указано') },
  {
    field: 'responsibilities',
    title: 'Обязанности',
    formatter: (value) => (value ? value.join(', ') : 'Не указано'),
  },
  { field: 'requirements', title: 'Требования', formatter: (value) => (value ? value.join(', ') : 'Не указано') },
];

const findOptionNameById = (options: { id: string; name: string }[], optionIds: string[]) => {
  return optionIds.map((id) => options.find((d) => d.id === id)?.name ?? '(не указано название)').join(', ');
};

export const VacancyData = () => {
  const { id } = useParams();
  const { data, refetch, isLoading } = useGetVacancyById(id!);

  const { data: universities, isLoading: isLoadingUniversities } = useGetUniversities();
  const { data: specialities, isLoading: isLoadingSpecialities } = useGetSpecialties();
  const { data: forms, isLoading: isLoadingForms } = useGetForms();
  const { data: statuses, isLoading: isLoadingStatuses } = useGetStatuses();
  const { data: statistic, isLoading: isLoadingStatistic } = useGetVacancyStatisticById(id!);

  const [toggleStatus] = useVacancyControllerTogglePublishMutation();

  if (isLoading || isLoadingUniversities || isLoadingSpecialities || isLoadingForms || isLoadingStatuses || !data) {
    return <LoadingSpinner />;
  }

  const setStatus = async (setIsPublished: boolean) => {
    if (!data?.id) return;

    await toggleStatus({ id: data.id });

    refetch();
  };

  return (
    <Box sx={{ backgroundColor: colors.white }} borderRadius={1} p={'20px'} mt={3}>
      <Grid display="flex" flexDirection="row" alignItems="flex-start" gap={indents.l}>
        {/* Левая колонка */}
        <Stack flex={1} divider={<Divider className={styles.divider} />}>
          {/* Тумблер публикации */}
          <Box display="flex" alignItems="center" gap={indents.xxs}>
            <Switch checked={data.isPublished} onChange={() => setStatus(!data.isPublished)} color="primary" />
            <Typography variant="body2">{data.isPublished ? 'Опубликовано' : 'Скрыто'}</Typography>
          </Box>

          <Box key="status" className={styles.row}>
            <Typography className={styles.rowTitle}>Статус вакансии:</Typography>
            <Typography variant="body2" color={data.response_url}>
              {data.status === 'published' ? 'Опубликована' : 'Архивирована'}
            </Typography>
          </Box>

          <Box key="href" className={styles.row}>
            <Typography className={styles.rowTitle}>Ссылка на вакансию в HRoom:</Typography>
            <Typography
              variant="body2"
              component="a"
              href={`${data.response_url}`}
              target="_blank"
              rel="noopener noreferrer">
              {data.response_url}
            </Typography>
          </Box>

          <Box key="href" className={styles.row}>
            <Typography className={styles.rowTitle}>Курс:</Typography>
            <Typography variant="body2" color={data.year_of_study ? colors.black : colors.grey}>
              {data.year_of_study}
            </Typography>
          </Box>

          <Box key="universities" className={styles.row}>
            <Typography className={styles.rowTitle}>Требуемые университеты:</Typography>
            <Typography variant="body2" color={data.university_ids ? colors.black : colors.grey}>
              {data.university_ids && universities
                ? data.university_ids.map((id) => universities.find((uni) => uni.id === id)?.shortName).join(', ')
                : 'Не указано'}
            </Typography>
          </Box>
          <Box key="specialities" className={styles.row}>
            <Typography className={styles.rowTitle}>Требуемые специальности:</Typography>
            <Typography variant="body2" color={data.specialty_ids ? colors.black : colors.grey}>
              {data.specialty_ids && specialities ? findOptionNameById(specialities, data.specialty_ids) : 'Не указано'}
            </Typography>
          </Box>
          <Box key="forms" className={styles.row}>
            <Typography className={styles.rowTitle}>Требуемые формы обучения:</Typography>
            <Typography variant="body2" color={data.education_form_ids ? colors.black : colors.grey}>
              {data.education_form_ids && forms ? findOptionNameById(forms, data.education_form_ids) : 'Не указано'}
            </Typography>
          </Box>
          <Box key="statuses" className={styles.row}>
            <Typography className={styles.rowTitle}>Требуемые статусы обучения:</Typography>
            <Typography variant="body2" color={data.academic_status_ids ? colors.black : colors.grey}>
              {data.academic_status_ids && statuses
                ? findOptionNameById(statuses, data.academic_status_ids)
                : 'Не указано'}
            </Typography>
          </Box>

          {/* Поля вакансии */}
          {fieldOptions.map(({ field, title, formatter, isCalculated }) => {
            const value = data[field as keyof VacancyResponse];
            const formatted =
              value !== undefined || isCalculated ? (formatter ? formatter(value, data) : value) : 'Не указано';

            return (
              <Box key={field} className={styles.row}>
                <Typography className={styles.rowTitle}>{title}:</Typography>
                <Typography variant="body2" color={value || isCalculated ? colors.black : colors.grey}>
                  {formatted}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {/* Правая колонка — статистика */}
        <Grid item flex={1} className={styles.right}>
          <Typography variant="h3" className={styles.rightTitle}>
            Статистика
          </Typography>
          {isLoadingStatistic && <LoadingSpinner />}
          {statistic && <VacancyStatistics data={statistic} />}
        </Grid>
      </Grid>
    </Box>
  );
};
